import React, { useState, useEffect } from 'react';
import './SegmentRoutesWindow.css';
import { API_BASE } from '../../config';

export const SegmentRoutesWindow = ({ onClose, onSegmentComplete }) => {
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [geofences, setGeofences] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [segmenting, setSegmenting] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [results, setResults] = useState(null);
  const [skipOverlapCheck, setSkipOverlapCheck] = useState(false);

  useEffect(() => {
    fetchVessels();
    fetchGeofences();
  }, []);

  // Pre-select all segment zones once geofences are loaded
  useEffect(() => {
    if (geofences.length > 0) {
      setSelectedZones(geofences.map(z => z.properties?.id).filter(Boolean));
    }
  }, [geofences]);

  const fetchVessels = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/vessels`);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Unexpected data format');
      setVessels(data);
      if (data.length === 0) setError('No vessels found. Upload vessel data first.');
    } catch (err) {
      setError(`Error loading vessels: ${err.message}`);
    }
  };

  const fetchGeofences = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/geofences`);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const data = await response.json();
      // DB stores 'segment' (renamed from 'segment_zone' in migration 005)
      const segmentZones = (data.features || []).filter(
        f => f.properties?.zone_type === 'segment' || f.properties?.zone_type === 'segment_zone'
      );
      setGeofences(segmentZones);
    } catch (err) {
      setError(`Error loading geofences: ${err.message}`);
    }
  };

  const handleSegmentRoutes = async () => {
    if (!selectedVessel) { setError('Please select a vessel'); return; }
    if (selectedZones.length === 0) { setError('Please select at least one segment zone'); return; }

    setSegmenting(true);
    setError(null);
    setSuccess(null);
    setResults(null);

    try {
      const body = {
        vessel_name: selectedVessel,
        segment_zones: selectedZones,
        overlap_threshold: 50,
      };
      if (startTime) body.start_time = startTime;
      if (endTime) body.end_time = endTime;

      const response = await fetch(`${API_BASE}/api/segment-routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.hint || 'Segmentation failed');
      if (data.error) throw new Error(data.error + (data.hint ? ` — ${data.hint}` : ''));

      console.log('[SegmentRoutes] Results:', {
        totalRawPoints: data.total_raw_points,
        runsCreated: data.runs_created,
        segmentedPoints: data.total_segmented_points,
        unsegmentedPoints: data.unsegmented_points,
        skipped: data.runs_skipped,
      });

      setResults(data);

      let msg = `Created ${data.runs_created} run(s)`;
      if (data.runs_skipped > 0) msg += `, skipped ${data.runs_skipped} duplicate(s)`;
      msg += `. ${data.total_segmented_points} segmented / ${data.unsegmented_points} unsegmented points.`;
      setSuccess(msg);

      if (data.runs_created > 0 && onSegmentComplete) {
        onSegmentComplete(data);
      }
    } catch (err) {
      setError(`Segmentation error: ${err.message}`);
    } finally {
      setSegmenting(false);
    }
  };

  const handleReprocessRoutes = async () => {
    if (!selectedVessel) { setError('Please select a vessel'); return; }
    if (selectedZones.length === 0) { setError('Please select at least one segment zone'); return; }
    if (!window.confirm(
      `This will delete ALL existing runs for "${selectedVessel}" and re-segment from scratch. Continue?`
    )) return;

    setReprocessing(true);
    setError(null);
    setSuccess(null);
    setResults(null);

    try {
      // Step 1: Delete all existing runs for the vessel
      const deleteResponse = await fetch(
        `${API_BASE}/api/vessels/${encodeURIComponent(selectedVessel)}/runs`,
        { method: 'DELETE' }
      );

      const deleteData = await deleteResponse.json();
      if (!deleteResponse.ok) throw new Error(deleteData.error || 'Failed to delete existing runs');

      console.log('[Reprocess] Deleted:', deleteData);

      // Step 2: Segment routes as normal
      const body = {
        vessel_name: selectedVessel,
        segment_zones: selectedZones,
        overlap_threshold: 50,
      };
      if (startTime) body.start_time = startTime;
      if (endTime) body.end_time = endTime;

      const segResponse = await fetch(`${API_BASE}/api/segment-routes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const segData = await segResponse.json();
      if (!segResponse.ok) throw new Error(segData.error || segData.hint || 'Segmentation failed');
      if (segData.error) throw new Error(segData.error + (segData.hint ? ` — ${segData.hint}` : ''));

      console.log('[Reprocess] Segmentation results:', segData);

      setResults(segData);

      let msg = `Deleted ${deleteData.runs_deleted} old run(s) and ${deleteData.zone_events_deleted} zone event(s). `;
      msg += `Created ${segData.runs_created} new run(s). `;
      msg += `${segData.total_segmented_points} segmented / ${segData.unsegmented_points} unsegmented points.`;
      setSuccess(msg);

      if (segData.runs_created > 0 && onSegmentComplete) {
        onSegmentComplete(segData);
      }
    } catch (err) {
      setError(`Reprocess error: ${err.message}`);
    } finally {
      setReprocessing(false);
    }
  };

  const handleToggleZone = (zoneId) => {
    setSelectedZones(prev =>
      prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId]
    );
  };

  const isBusy = segmenting || reprocessing;

  return (
    <div className="segment-routes-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="segment-routes-window">
        <div className="segment-routes-header">
          <h2>Segment Routes with Geofences</h2>
          <button className="close-btn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="segment-routes-content">
          {/* 1. Vessel Selection */}
          <div className="form-section">
            <h3>1. Select Vessel</h3>
            <select
              value={selectedVessel}
              onChange={(e) => { setSelectedVessel(e.target.value); setResults(null); setError(null); setSuccess(null); }}
              className="form-select"
            >
              <option value="">-- Select Vessel --</option>
              {vessels.map(vessel => (
                <option key={vessel.vessel_name} value={vessel.vessel_name}>
                  {vessel.vessel_name} ({vessel.has_runs ? `${vessel.run_count} runs` : 'Raw points only'})
                </option>
              ))}
            </select>
          </div>

          {/* 2. Time Range */}
          <div className="form-section">
            <h3>2. Time Range (Optional)</h3>
            <div className="time-range-inputs">
              <div className="form-group">
                <label>Start Time</label>
                <input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="form-input" />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="form-input" />
              </div>
            </div>
            <p className="help-text">Leave blank to use all available data.</p>
          </div>

          {/* 3. Segment Zones */}
          <div className="form-section">
            <h3>3. Select Segment Zones</h3>
            {geofences.length === 0 ? (
              <p className="warning-text">
                ⚠️ No segment zones found. Create segment zones in the Zone Manager first.
              </p>
            ) : (
              <div className="zone-list">
                {geofences.map(zone => {
                  const zoneId = zone.properties?.id;
                  return (
                    <label key={zoneId} className="zone-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedZones.includes(zoneId)}
                        onChange={() => handleToggleZone(zoneId)}
                      />
                      <div>
                        <div className="zone-name">
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#8b5cf6', marginRight: 8 }} />
                          {zone.properties?.name || 'Unnamed zone'}
                        </div>
                        {zone.properties?.description && (
                          <div className="zone-description">{zone.properties.description}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* 4. Deduplication */}
          <div className="form-section">
            <h3>4. Deduplication</h3>
            <label className="checkbox-label">
              <input type="checkbox" checked={skipOverlapCheck} onChange={(e) => setSkipOverlapCheck(e.target.checked)} />
              <span>Skip overlap detection (faster, may create duplicates)</span>
            </label>
          </div>

          {/* Segment Button */}
          <div className="form-section">
            <button onClick={handleSegmentRoutes} disabled={isBusy || !selectedVessel || selectedZones.length === 0} className="btn btn-primary">
              {segmenting ? '⏳ Segmenting...' : '🔀 Segment Routes'}
            </button>
          </div>

          {/* Reprocess Button */}
          <div className="form-section">
            <button onClick={handleReprocessRoutes} disabled={isBusy || !selectedVessel || selectedZones.length === 0} className="btn btn-danger">
              {reprocessing ? '⏳ Reprocessing...' : '🔄 Reprocess Routes'}
            </button>
            <p className="help-text">Deletes all existing runs for the selected vessel, then re-segments from scratch.</p>
          </div>

          {/* Messages */}
          {error && <div className="message message-error">❌ {error}</div>}
          {success && <div className="message message-success">✅ {success}</div>}

          {/* Results */}
          {results && (
            <div className="form-section">
              <h4>Results Summary</h4>
              <div className="results-box">
                <div><strong>Raw Points:</strong> {results.total_raw_points?.toLocaleString()}</div>
                <div><strong>Runs Created:</strong> {results.runs_created}</div>
                <div><strong>Segmented Points:</strong> {results.total_segmented_points?.toLocaleString()}</div>
                <div><strong>Unsegmented Points:</strong> {results.unsegmented_points?.toLocaleString()}</div>
                {results.runs_skipped > 0 && (
                  <div><strong>Skipped (duplicates):</strong> {results.runs_skipped}</div>
                )}
                {results.runs && results.runs.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <strong>Created Runs:</strong>
                    {results.runs.map((run) => (
                      <div key={run.run_id} className="run-item">
                        • {run.run_name} — {run.point_count} pts, {run.distance_nm?.toFixed(2)} NM
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="instructions">
            <h4>How it works:</h4>
            <ol>
              <li>Select a vessel and optionally narrow the time range</li>
              <li>Select one or more <strong>segment zones</strong> (created in Zone Manager)</li>
              <li>Click <strong>Segment Routes</strong> — runs are created between zone exits and entries</li>
              <li>Click <strong>Reprocess Routes</strong> to delete all existing runs and re-segment from scratch</li>
            </ol>
            <p>
              <strong>Tip:</strong> A run starts when the vessel <em>exits</em> a segment zone and ends
              when it <em>enters</em> any segment zone. Points outside all zones that fall between
              zone transitions form the segmented runs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentRoutesWindow;